import { getUsersSearch } from "@/actions";
import { useSetQuery } from "@/hooks";
import { SearchParams, UsersSearchState } from "@/types";
import { useActionState, useCallback, useEffect, useRef, useState } from "react";

export const useUsersSearch = (params: SearchParams) => {
  const initialState: UsersSearchState = { params, query: "", users: [] };
  const [state, action, isPending] = useActionState(getUsersSearch, initialState);
  const { setQuery, isPending: isQueryPending } = useSetQuery();
  const [value, setValue] = useState("");
  const formRef = useRef<HTMLFormElement>(null);
  const timer = useRef<number | undefined>(undefined);
  const q = value.trim();
  const isQueryReady = q.length >= 3;
  const isFresh = state.query === q;
  const hasUsers = isFresh && state.users.length > 0;
  const showNotFound = isQueryReady && isFresh && !isPending && state.users.length === 0;
  const open = isQueryReady && (isPending || hasUsers || showNotFound);

  useEffect(() => {
    window.clearTimeout(timer.current);
    if (!isQueryReady) return;
    timer.current = window.setTimeout(() => {
      formRef.current?.requestSubmit();
    }, 300);
    return () => window.clearTimeout(timer.current);
  }, [isQueryReady, q]);

  const handleUserClick = useCallback(
    (userId: string) => {
      setValue("");
      setQuery({ user_id: userId }, { reset: true });
    },
    [setQuery],
  );

  return {
    open,
    formRef,
    action,
    isPending,
    value,
    hasUsers,
    showNotFound,
    state,
    setValue,
    handleUserClick,
    isQueryPending,
  };
};
